/**
 * GroupMembersModal Component
 *
 * Modal for viewing and managing members in a workspace group
 */

import React, { useState, useEffect } from 'react';
import { Workspace, WorkspaceGroup, WorkspaceGroupMember, WorkspaceMember } from '../../../types/workspace';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { X, Users, Plus, Trash2, Loader2 } from 'lucide-react';
import { AddGroupMemberModal } from './AddGroupMemberModal';
import { ConfirmModal } from './ConfirmModal';

interface GroupMembersModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspace: Workspace;
  group: WorkspaceGroup;
}

export const GroupMembersModal: React.FC<GroupMembersModalProps> = ({
  isOpen,
  onClose,
  workspace,
  group,
}) => {
  const { loadGroupMembers, removeGroupMember, loadWorkspaceMembers } = useWorkspace();
  const [members, setMembers] = useState<WorkspaceGroupMember[]>([]);
  const [workspaceMembers, setWorkspaceMembers] = useState<WorkspaceMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingMemberId, setDeletingMemberId] = useState<number | null>(null);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<WorkspaceGroupMember | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const loadMembers = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [groupMembersData, workspaceMembersData] = await Promise.all([
        loadGroupMembers(workspace.id, group.id),
        loadWorkspaceMembers(workspace.id),
      ]);
      setMembers(groupMembersData);
      setWorkspaceMembers(workspaceMembersData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao carregar membros');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadMembers();
    }
  }, [isOpen, workspace.id, group.id]);

  const handleRemoveMemberClick = (member: WorkspaceGroupMember) => {
    setMemberToDelete(member);
    setShowDeleteConfirm(true);
  };

  const handleRemoveMember = async () => {
    if (!memberToDelete) return;

    setDeletingMemberId(memberToDelete.user_id);
    setErrorMessage(null);

    try {
      await removeGroupMember(workspace.id, group.id, memberToDelete.user_id);
      setMembers((prev) => prev.filter((m) => m.user_id !== memberToDelete.user_id));
      setShowDeleteConfirm(false);
      setMemberToDelete(null);
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Falha ao remover membro');
    } finally {
      setDeletingMemberId(null);
    }
  };

  const getPermissionsList = (member: WorkspaceGroupMember): string[] => {
    // Groups no longer have permissions - show workspace-level permissions instead
    const workspaceMember = workspaceMembers.find(wm => wm.user_id === member.user_id);
    if (!workspaceMember || !workspaceMember.permissions) {
      return [];
    }

    const permissions: string[] = [];
    if (workspaceMember.permissions.can_admin) permissions.push('Administrador');
    if (workspaceMember.permissions.can_view_analytics) permissions.push('Ver Analytics');
    if (workspaceMember.permissions.can_manage_agents) permissions.push('Gerenciar Agentes');
    if (workspaceMember.permissions.can_manage_workspace) permissions.push('Gerenciar Workspace');
    return permissions;
  };

  // Get members that are not already in the group
  const availableMembers = workspaceMembers.filter(
    (wm) => !members.some((gm) => gm.user_id === wm.user_id)
  );

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 w-screen h-screen bg-black/60 z-40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-4xl bg-[#2a2a2a] rounded-2xl shadow-2xl z-50 max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#333333]">
          <div>
            <h2 className="text-xl font-semibold text-white">{group.name} - Membros</h2>
            <p className="text-sm text-[#888888] mt-1">
              {members.length} membro{members.length !== 1 ? 's' : ''} neste grupo
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-[#888888] hover:text-white hover:bg-[#333333] rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Error Message */}
          {(error || errorMessage) && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {error || errorMessage}
            </div>
          )}

          {/* Add Member Button */}
          <div className="mb-4">
            <button
              onClick={() => setShowAddMemberModal(true)}
              disabled={availableMembers.length === 0}
              className="
                flex items-center gap-2 px-4 py-2
                bg-[#F4CC04] hover:bg-[#D4B104]
                text-black font-medium
                rounded-lg transition-colors
                disabled:opacity-50 disabled:cursor-not-allowed
              "
              title={availableMembers.length === 0 ? 'Nenhum membro disponível para adicionar' : 'Adicionar membro ao grupo'}
            >
              <Plus className="w-4 h-4" />
              <span>Adicionar Membro</span>
            </button>
          </div>

          {/* Loading State */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#F4CC04] animate-spin" />
            </div>
          ) : members.length === 0 ? (
            /* Empty State */
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-[#888888] mx-auto mb-3" />
              <p className="text-[#888888] mb-4">Nenhum membro neste grupo ainda</p>
              <button
                onClick={() => setShowAddMemberModal(true)}
                disabled={availableMembers.length === 0}
                className="text-[#F4CC04] hover:text-[#D4B104] font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Adicionar seu primeiro membro
              </button>
            </div>
          ) : (
            /* Members Table */
            <div className="border border-[#333333] rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#1a1a1a] border-b border-[#333333]">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-white">
                      Usuário
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-white">
                      Permissões no Workspace
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-white">
                      Adicionado ao Grupo
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-white">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#333333]">
                  {members.map((member) => {
                    const permissions = getPermissionsList(member);
                    return (
                      <tr key={member.id} className="hover:bg-[#1a1a1a] transition-colors">
                        <td className="py-3 px-4">
                          <div>
                            <div className="font-medium text-white">
                              {member.user_name || `User ${member.user_id}`}
                            </div>
                            {member.user_email && (
                              <div className="text-sm text-[#888888]">{member.user_email}</div>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex flex-wrap gap-1">
                            {permissions.length > 0 ? (
                              permissions.map((perm) => (
                                <span
                                  key={perm}
                                  className="px-2 py-1 bg-[#F4CC04]/20 text-[#F4CC04] rounded text-xs"
                                >
                                  {perm}
                                </span>
                              ))
                            ) : (
                              <span className="text-sm text-[#888888]">Sem permissões</span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-[#888888]">
                          {new Date(member.created_at).toLocaleDateString('pt-BR')}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <button
                            onClick={() => handleRemoveMemberClick(member)}
                            disabled={deletingMemberId === member.user_id}
                            className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg disabled:opacity-50 transition-colors"
                            title="Remover do grupo"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-[#333333]">
          <button
            onClick={onClose}
            className="
              px-4 py-2
              text-[#888888] hover:text-white
              hover:bg-[#333333]
              rounded-lg transition-colors
            "
          >
            Fechar
          </button>
        </div>
      </div>

      {/* Add Member Modal */}
      <AddGroupMemberModal
        isOpen={showAddMemberModal}
        onClose={() => setShowAddMemberModal(false)}
        workspace={workspace}
        group={group}
        availableMembers={availableMembers}
        onSuccess={() => {
          loadMembers();
          setShowAddMemberModal(false);
        }}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setMemberToDelete(null);
          setErrorMessage(null);
        }}
        onConfirm={handleRemoveMember}
        title="Remover do Grupo"
        message={`Tem certeza que deseja remover "${memberToDelete?.user_name || 'este membro'}" do grupo "${group.name}"?\n\nO membro permanecerá no workspace, mas perderá acesso aos agentes específicos deste grupo.`}
        confirmText="Remover"
        cancelText="Cancelar"
        type="warning"
        isProcessing={deletingMemberId === memberToDelete?.user_id}
      />
    </>
  );
};

export default GroupMembersModal;
